import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { UserGroupMemberFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import {
  UserGroupFormDto,
  type UserGroupFormInterface
} from '@zaoblako/shared/modules/security/dtos/user-group/user-group-form.dto';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { UserGroupService } from '@zaoblako/web/core/services/security/user-group.service';
import { UserGroupHistoryComponent } from '@zaoblako/web/modules/security/dumbs/user-group-form/user-group-history/user-group-history.component';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { TableComponent } from '@zaoblako/web/shared/dumbs/table/table.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-user-group-form',
  templateUrl: './user-group-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    UserMultiSelectComponent,
    ButtonComponent,
    UserGroupHistoryComponent,
    SpinnerComponent,
    UserGroupHistoryComponent,
    TableComponent,
    CheckboxModule,
    FormsModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserGroupFormComponent extends BaseFormComponent<UserGroupFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public userGroup = <UserGroupFormInterface>{};
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public dto = UserGroupFormDto;
  public columns: CrmTableColumn[] = [{ label: 'Пользователь' }, { label: 'Руководитель' }];

  public constructor(
    private readonly userGroupService: UserGroupService,
    public readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public toUser(user: any): UserGroupMemberFindInterface {
    return user as UserGroupMemberFindInterface;
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.userGroupService
      .findById<UserGroupFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (userGroup) => {
          this.userGroup = { ...userGroup, users: userGroup.users?.filter((user) => !user?.deleted) };
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveUserGroup() {
    if (this.checkValidate(this.userGroup).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.userGroupService.update<UserGroupFormInterface, UserGroupFindInterface>(this.userGroup).subscribe({
          next: (userGroup) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Группа "${this.userGroup?.name}" обновлена`);
            this.formStoreService.saveNext(userGroup, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.userGroupService.create<UserGroupFindInterface>(this.userGroup).subscribe({
          next: (userGroup) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Группа создана');
            this.formStoreService.saveNext(userGroup, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteUserGroup() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить группу "${this.userGroup?.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.userGroupService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Группа "${this.userGroup?.name}" удалена`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }
}
